import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import QRCodeModal from "../components/QRCodeModal";
import ChipHealthModal from "../components/ChipHealthModal";
import ConfirmModal from "../components/ConfirmModal";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Smartphone, MessageSquare, Activity, HeartPulse, Plus, Trash2, QrCode, Flame, Phone, Video, Users, Mic, WifiOff } from "lucide-react";
import { toast } from "sonner";

const TOOLTIP_CONTENT_STYLE = { backgroundColor: "#0F172A", border: "1px solid #1E293B", borderRadius: 10, color: "#F8FAFC" };
const TOOLTIP_LABEL_STYLE = { color: "#94A3B8" };

function StatusBadge({ chip }) {
  let label = "Desconectado";
  let cls = "bg-red-500/15 text-red-300 border-red-500/30";
  if (chip.status === "desconectado") {
    label = "Sessão caiu";
    cls = "bg-red-500/15 text-red-300 border-red-500/30";
  } else if (chip.status === "aguardando_qr") {
    label = "Aguardando QR";
    cls = "bg-amber-500/15 text-amber-300 border-amber-500/30";
  } else if (chip.status === "conectado" && chip.warmup_enabled) {
    label = "Aquecendo";
    cls = "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  } else if (chip.status === "conectado") {
    label = "Conectado";
    cls = "bg-blue-500/15 text-blue-300 border-blue-500/30";
  }
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider border ${cls}`} data-testid={`chip-status-badge-${chip.id}`}>
      {label}
    </span>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [chips, setChips] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [healthChip, setHealthChip] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [resumeChip, setResumeChip] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/stats/dashboard");
      setStats(data);
    } catch (e) {
      console.error("Falha ao atualizar estatísticas do dashboard", e);
    }
  }, []);

  const fetchChips = useCallback(async () => {
    try {
      const { data } = await api.get("/chips");
      setChips(data);
    } catch (e) {
      console.error("Falha ao atualizar lista de chips", e);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchChips();
    const t = setInterval(() => {
      fetchStats();
      fetchChips();
    }, 10000);
    return () => clearInterval(t);
  }, [fetchStats, fetchChips]);

  const toggleWarmup = async (chip) => {
    try {
      await api.patch(`/chips/${chip.id}/config`, { warmup_enabled: !chip.warmup_enabled });
      toast.success(chip.warmup_enabled ? `Aquecimento pausado em ${chip.apelido}` : `Aquecimento ativado em ${chip.apelido}`);
      fetchChips();
      fetchStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeChip = (chip) => setRemoveTarget(chip);

  const confirmRemoveChip = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await api.delete(`/chips/${removeTarget.id}`);
      toast.success("Chip removido e desconectado");
      fetchChips();
      fetchStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRemoving(false);
      setRemoveTarget(null);
    }
  };

  const openQr = (chip = null) => {
    setResumeChip(chip);
    setQrOpen(true);
  };

  const cards = useMemo(
    () =>
      stats
        ? [
            { testid: "stat-card-connected-chips", icon: Smartphone, label: "Chips Conectados", value: `${stats.chips_connected} / ${stats.chips_limit}`, sub: stats.plan_label, color: "text-emerald-400" },
            { testid: "stat-card-messages-today", icon: MessageSquare, label: "Mensagens Trocadas Hoje", value: stats.messages_today.toLocaleString("pt-BR"), sub: "loop automatizado", color: "text-blue-400" },
            { testid: "stat-card-loop-status", icon: Activity, label: "Status do Loop", value: stats.loop_status, sub: `${stats.chips_warming} chip(s) aquecendo`, color: stats.loop_status === "Ativo" ? "text-emerald-400" : "text-amber-400" },
            { testid: "stat-card-health", icon: HeartPulse, label: "Saúde Média dos Chips", value: `${stats.health}%`, sub: "risco de banimento baixo", color: "text-purple-400" },
          ]
        : [],
    [stats]
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Seu aquecimento roda na nuvem 24/7, mesmo com o computador desligado.</p>
        </div>
        <Button
          onClick={() => openQr()}
          data-testid="btn-open-qr-modal"
          className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold shadow-[0_0_20px_rgba(16,185,129,0.25)] transition-all duration-200 hover:-translate-y-0.5"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Novo Chip
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {cards.map((c, i) => (
          <div
            key={c.testid}
            data-testid={c.testid}
            className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-700 hover:shadow-lg animate-fade-up"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{c.label}</span>
              <c.icon className={`w-5 h-5 ${c.color}`} />
            </div>
            <p className={`text-3xl font-extrabold tracking-tight font-mono ${c.color}`}>{c.value}</p>
            <p className="text-xs text-slate-500 mt-1.5">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6">
          <h2 className="font-heading text-lg font-semibold text-slate-100 mb-1">Mensagens nos últimos 7 dias</h2>
          <p className="text-xs text-slate-500 mb-5">Histórico do loop de aquecimento entre seus chips</p>
          <div className="h-64" data-testid="chart-messages-7d">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.chart || []} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="msgGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="dia" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
                <Area type="monotone" dataKey="mensagens" stroke="#10B981" strokeWidth={2.5} fill="url(#msgGradient)" name="Mensagens" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot" />
            <h2 className="font-heading text-lg font-semibold text-slate-100">Atividade em tempo real</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Últimas trocas do motor de maturação</p>
          <div className="space-y-3 overflow-y-auto max-h-64 pr-1" data-testid="activity-feed">
            {stats?.activity?.length ? (
              stats.activity.map((a) => (
                <div key={`${a.kind}-${a.id}`} className="bg-slate-900/60 border border-slate-800/80 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-emerald-300 truncate flex items-center gap-1.5">
                      {a.kind === "chamada" ? (
                        a.tipo === "video" ? <Video className="w-3.5 h-3.5 text-purple-400 shrink-0" /> : <Phone className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      ) : a.grupo_id ? (
                        <Users className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      ) : null}
                      {a.from_name} → {a.to_name}
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono shrink-0">
                      {new Date(a.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  {a.kind === "chamada" ? (
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Chamada de {a.tipo === "video" ? "vídeo" : "voz"} concluída · {a.duracao_s}s
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{a.texto}</p>
                      {(a.grupo_id || ["imagem", "video", "figurinha", "audio"].includes(a.tipo)) && (
                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                          {a.grupo_id && a.tipo !== "entrada_grupo" && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider bg-amber-500/10 text-amber-300 border border-amber-500/30">
                              Grupo · {a.grupo_nome}
                            </span>
                          )}
                          {a.tipo === "entrada_grupo" && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider bg-amber-500/10 text-amber-300 border border-amber-500/30">
                              Entrada em grupo
                            </span>
                          )}
                          {["imagem", "video", "figurinha", "audio"].includes(a.tipo) && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
                              {a.tipo === "audio" && <Mic className="w-3 h-3" />}
                              {a.tipo === "imagem" ? "Imagem" : a.tipo === "video" ? "Vídeo" : a.tipo === "figurinha" ? "Figurinha" : `Áudio · gravando ${a.gravando_s || 3}s`}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500 text-sm">
                <Flame className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                Conecte pelo menos 2 chips para o loop começar.
              </div>
            )}
          </div>
        </div>
      </div>

      {chips.some((c) => c.status === "desconectado") && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-200 flex items-center gap-2.5" data-testid="session-lost-banner">
          <WifiOff className="w-5 h-5 shrink-0 text-red-400" />
          <span>
            A sessão de {chips.filter((c) => c.status === "desconectado").length} chip(s) caiu no WhatsApp.
            Clique em <strong>Reconectar</strong> no chip para escanear o QR Code novamente.
          </span>
        </div>
      )}

      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-100 mb-4">Meus Chips ({chips.length})</h2>
        {chips.length === 0 ? (
          <div className="bg-[#161F32] border border-dashed border-slate-700 rounded-xl p-10 text-center" data-testid="chips-empty-state">
            <Smartphone className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-300 font-medium mb-1">Nenhum chip conectado ainda</p>
            <p className="text-sm text-slate-500 mb-5">Escaneie o QR Code com o WhatsApp para começar o aquecimento.</p>
            <Button onClick={() => openQr()} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
              <QrCode className="w-4 h-4 mr-2" /> Conectar primeiro chip
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {chips.map((chip) => (
              <div
                key={chip.id}
                data-testid={`chip-card-item-${chip.id}`}
                className="bg-[#161F32] border border-slate-800 rounded-xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-700 hover:shadow-lg"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {chip.avatar ? (
                      <img src={chip.avatar} alt={chip.apelido} className="w-11 h-11 rounded-full bg-slate-800 border border-slate-700" />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-slate-500" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-100 truncate">{chip.apelido}</p>
                      <p className="text-xs text-slate-500 font-mono truncate">{chip.number || "aguardando pareamento"}</p>
                    </div>
                  </div>
                  <StatusBadge chip={chip} />
                  {chip.status === "conectado" && chip.connected_at && (Date.now() - new Date(chip.connected_at).getTime()) < 72 * 3600 * 1000 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30"
                      title="Modo ramp-up anti-ban: volume reduzido nos primeiros 3 dias após a conexão"
                      data-testid={`chip-rampup-badge-${chip.id}`}>RAMP-UP</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                  <span className="font-mono">{chip.total_messages || 0} msgs enviadas</span>
                  {chip.health != null && (
                    <span className="flex items-center gap-1.5" data-testid={`chip-health-inline-${chip.id}`}>
                      <span className="w-16 h-1.5 progress-track inline-block"><span className="block h-1.5 progress-fill" style={{ width: `${chip.health}%` }} /></span>
                      <span className={`font-mono font-semibold ${chip.health >= 80 ? "text-emerald-400" : chip.health >= 50 ? "text-amber-400" : "text-red-400"}`}>{chip.health}%</span>
                    </span>
                  )}
                  <span className="font-mono">delay {chip.delay_min}s–{chip.delay_max}s</span>
                </div>
                <div className="flex items-center justify-between gap-2 pt-4 border-t border-slate-800">
                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    <Switch
                      checked={chip.warmup_enabled}
                      onCheckedChange={() => toggleWarmup(chip)}
                      disabled={chip.status !== "conectado"}
                      data-testid={`chip-toggle-warmup-${chip.id}`}
                    />
                    Aquecer
                  </label>
                  <div className="flex gap-1.5">
                    {chip.status === "aguardando_qr" && (
                      <Button size="sm" variant="outline" onClick={() => openQr(chip)}
                        data-testid={`chip-rescan-qr-${chip.id}`}
                        className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-8 text-xs">
                        <QrCode className="w-3.5 h-3.5 mr-1" /> Ver QR
                      </Button>
                    )}
                    {chip.status === "desconectado" && (
                      <Button size="sm" variant="outline" onClick={() => openQr(chip)}
                        data-testid={`chip-reconnect-${chip.id}`}
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 text-xs">
                        <QrCode className="w-3.5 h-3.5 mr-1" /> Reconectar
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setHealthChip(chip)}
                      data-testid={`chip-health-button-${chip.id}`}
                      className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 h-8 text-xs">
                      <Activity className="w-3.5 h-3.5 mr-1" /> Saúde
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => removeChip(chip)}
                      data-testid={`chip-remove-button-${chip.id}`}
                      className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 text-xs">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remover
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <QRCodeModal
        open={qrOpen}
        onClose={() => { setQrOpen(false); setResumeChip(null); fetchChips(); }}
        onConnected={() => { fetchChips(); fetchStats(); }}
        existingChip={resumeChip}
      />
      <ChipHealthModal chip={healthChip} onClose={() => setHealthChip(null)} />
      <ConfirmModal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemoveChip}
        title={removeTarget ? `Remover o chip "${removeTarget.apelido}"?` : ""}
        message="A sessão será desconectada na Evolution API e o loop deste chip para imediatamente. Esta ação não pode ser desfeita."
        confirmLabel="Remover chip"
        loading={removing}
        testid="dashboard-remove-chip-modal"
      />
    </div>
  );
}
